import Vue from 'vue'
import RealtimeHub from '@/services/RealTime/RealtimeHub'
import { makeRealtimeLogger } from '@/services/RealTime/realtimeCommon'

const state = Vue.observable({
  started: false
})

const RealtimeProvider = {
  name: 'RealtimeProvider',
  props: {
    user: {
      type: Object,
      default: null
    },
    manageLifecycle: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    _startedOnce: false,
    _logger: null
  }),
  methods: {
    _debug (...args) {
      if (!this._logger) {
        this._logger = makeRealtimeLogger('[Realtime][RealtimeProvider]', (this.$root && this.$root.$log) || null)
      }
      if (this._logger) {
        this._logger(...args)
      } else {
        const prefix = '[Realtime][RealtimeProvider]'
        try {
          // eslint-disable-next-line no-console
          console.log(prefix, ...args)
        } catch {
          // ignore
        }
      }
    },

    async _ensureStarted (reason) {
      this._debug('_ensureStarted', { reason: reason || 'unknown' })
      // Only the instance owning the realtime lifecycle should start the hub.
      if (this.manageLifecycle !== true) {
        return
      }

      // User presence is required to start; wrappers may omit the prop.
      if (!this.user) {
        return
      }

      if (!this._startedOnce) {
        this._startedOnce = true
        try {
          RealtimeHub.init({
            log: (...args) => {
              this._debug(...args)
            }
          })
        } catch {
          // ignore
        }
      }

      try {
        await RealtimeHub.start()
        state.started = true
        this._debug('started', { reason: reason || 'unknown' })
      } catch (e) {
        this._debug('start failed', e)
      }
    },

    _stop (reason) {
      try {
        RealtimeHub.stop()
      } catch {
        // ignore
      }

      state.started = false
      this._debug('stopped', { reason: reason || 'unknown' })
    },

    async subscribe (opts) {
      return await RealtimeHub.subscribe(opts)
    },

    onReconnected (handler) {
      return RealtimeHub.onReconnected(handler)
    },

    onResubscribed (handler) {
      return RealtimeHub.onResubscribed(handler)
    },

    stop () {
      this._stop('manual')
    }
  },
  watch: {
    user: {
      handler (newValue, oldValue) {
        this._debug('watch user:', newValue, oldValue)

        // Only the instance owning the realtime lifecycle should start/stop the hub.
        if (this.manageLifecycle !== true) {
          return
        }

        const hasNew = Boolean(newValue)
        const hasOld = Boolean(oldValue)

        // User became present.
        if (hasNew && !hasOld) {
          this._ensureStarted('user_present')
        }

        // User became absent.
        if (!hasNew && hasOld) {
          if (state.started) {
            this._stop('user_absent')
          }
        }
      },
      immediate: true
    }
  },
  mounted () {
    this._debug('mounted', { manageLifecycle: this.manageLifecycle })
  },
  beforeDestroy () {
    this._debug('beforeDestroy', { manageLifecycle: this.manageLifecycle })
    if (this.manageLifecycle) {
      this._stop('destroy')
    }
  },
  render () {
    if (this.$scopedSlots.default !== undefined) {
      return this.$scopedSlots.default({
        state,
        methods: {
          subscribe: this.subscribe,
          onReconnected: this.onReconnected,
          onResubscribed: this.onResubscribed,
          stop: this.stop
        }
      })
    } else {
      return this.$slots.default
    }
  }
}

export default RealtimeProvider
